import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import TopBar from '../../global/TopBar';
import { MagnifyingGlassIcon, ShoppingCartIcon } from '@heroicons/react/24/outline';
import { motion, AnimatePresence } from 'framer-motion';
import { useSelector } from 'react-redux';
const ClothesStore = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [clothesItems, setClothesItems] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isSearched, setIsSearched] = useState(false);
  const [initialItems, setInitialItems] = useState([]);
  const token = useSelector((state) => state.auth.token);

  useEffect(() => {
    fetchInitialItems();
  }, []);

  const fetchInitialItems = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_STORE_API_URL}/clothes?limit=10&city=newbury`,
        {
          headers: {
            "Content-Type": "application/json",
            "Api-Name": "store-api",
            "Authorization": `Bearer ${token}`
          }
        }
      );
      if (!response.ok) {
        throw new Error('Network response was not ok');
      }
      const data = await response.json();
      setClothesItems(data.items);
      setInitialItems(data.items);
      setFilteredItems(data.items);
      setError(null);
    } catch (err) {
      setError('Failed to fetch clothes items. Please try again later.');
      console.error('Error fetching clothes:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchClothesItems = async (query = '') => {
    setLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_STORE_API_URL}/clothes?item=${query}&city=newbury`,
        {
          headers: {
            "Content-Type": "application/json",
            "Api-Name": "store-api",
            "Authorization": `Bearer ${token}`
          }
        }
      );
      if (!response.ok) {
        throw new Error('Network response was not ok');
      }
      const resp_data = await response.json();
      setClothesItems(resp_data.items);
      setFilteredItems(resp_data.items);
      setIsSearched(true);
      setError(null);
    } catch (err) {
      setError('Failed to fetch clothes items. Please try again later.');
      console.error('Error fetching clothes:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    if (searchQuery.trim()) {
      fetchClothesItems(searchQuery);
    } else {
      handleClearSearch();
    }
  };

  const handleClearSearch = () => {
    setSearchQuery('');
    setIsSearched(false);
    setClothesItems(initialItems);
    setFilteredItems(initialItems);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-pri-dark to-sec-dark flex items-center justify-center">
        <motion.div
          className="w-16 h-16 border-4 border-pri-yellow border-t-transparent rounded-full"
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-pri-dark to-sec-dark">
      <TopBar />
      <div className="container mx-auto px-4 py-12">
        {/* Header */}
        <motion.h1 
          className="text-4xl sm:text-5xl font-bold text-center bg-gradient-to-r from-pri-yellow to-pri-green text-transparent bg-clip-text mb-8"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          Trendy Clothes at Your Fingertips
        </motion.h1>

        {/* Search Bar */}
        <motion.div 
          className="max-w-2xl mx-auto mb-12 relative"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          <div className="relative">
            <MagnifyingGlassIcon className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-pri-gray" />
            <input
              type="text"
              placeholder="Search for clothes..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              className="w-full px-4 py-3 pl-12 pr-24 bg-sec-dark/50 backdrop-blur-sm rounded-full text-pri-white border border-pri-gray/20 focus:outline-none focus:ring-2 focus:ring-pri-yellow"
            />
            <button 
              onClick={handleSearch}
              className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-gradient-to-r from-pri-yellow to-pri-green text-pri-dark px-6 py-2 rounded-full font-medium hover:opacity-90 transition-all duration-300"
            >
              Search
            </button>
          </div>
        </motion.div>

        {isSearched && (
          <motion.div 
            className="text-center mb-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3 }}
          >
            <button
              onClick={handleClearSearch}
              className="text-pri-yellow hover:text-pri-yellow-light transition-colors duration-200"
            >
              Clear Search
            </button>
          </motion.div>
        )}

        {error && (
          <motion.p 
            className="text-center text-red-500 mb-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3 }}
          >
            {error}
          </motion.p>
        )}

        {/* Items Grid */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6"
        >
          {filteredItems.map((item, index) => (
            <motion.div
              key={item.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="bg-sec-dark/50 backdrop-blur-sm rounded-xl overflow-hidden shadow-lg group"
            >
              <div className="relative aspect-w-16 aspect-h-9">
                <img 
                  src={item.image} 
                  alt={item.name}
                  className="w-full h-48 object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/0 to-transparent" />
              </div>

              <div className="p-4">
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <h3 className="text-xl font-bold text-pri-white">{item.name}</h3>
                    <p className="text-sm text-pri-gray">{item.store_name}</p>
                  </div>
                  <div className="bg-gradient-to-r from-pri-yellow to-pri-green text-pri-dark px-3 py-1 rounded-full text-sm font-bold">
                    £{item.price.toFixed(2)} / {item.unit}
                  </div>
                </div>

                <div className="flex items-center justify-between mt-4">
                  <Link
                    to={`/store/clothes/storefront/${item.store_id}`}
                    className="text-pri-yellow font-medium hover:text-pri-green transition-colors duration-300 flex items-center"
                  >
                    View Store
                    <motion.span
                      className="inline-block ml-2"
                      animate={{ x: [0, 5, 0] }}
                      transition={{ duration: 1.5, repeat: Infinity }}
                    >
                      →
                    </motion.span>
                  </Link>
                  <motion.button
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    className="p-2 rounded-full bg-pri-dark/30"
                  >
                    <ShoppingCartIcon className="h-5 w-5 text-pri-yellow" />
                  </motion.button>
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </div>
  );
};

export default ClothesStore;
